import type { CSSProperties, ReactNode } from 'react';
import { Close, Modal, SheetFrame, ShieldCheck } from '../../design-system';
import { SleekButton, useIsSleek } from '../../theme';

export interface FairnessSheetProps {
  /** Design Constructor inspect name of the centred (non-sleek) dialog. */
  inspectName: string;
  titleId: string;
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** Buttons pinned under the content. */
  footer: ReactNode;
}

// The seed fields, roll cards and verdict read these, so one set of inner
// pieces serves both looks.
const SLEEK_TONES = {
  '--fairness-text': 'var(--co-FairnessSheet-text, #FFFFFF)',
  '--fairness-muted': 'var(--co-FairnessSheet-mutedText, #6D707C)',
  '--fairness-surface': 'var(--co-FairnessSheet-surface, #1A1B1F)',
  '--fairness-border': 'transparent',
  '--fairness-ok': 'var(--co-FairnessSheet-text, #FFFFFF)',
  '--fairness-bad': 'var(--ui-danger, #E5484D)',
} as CSSProperties;

const LIGHT_TONES = {
  '--fairness-text': 'var(--ui-text, #15181E)',
  '--fairness-muted': 'var(--ui-text-subtext, #878B95)',
  '--fairness-surface': 'transparent',
  '--fairness-border': 'var(--ui-border, #EAEAEB)',
  '--fairness-ok': 'var(--ui-accent, #25EA1E)',
  '--fairness-bad': 'var(--ui-danger, #E5484D)',
} as CSSProperties;

/**
 * The dialog every provably-fair screen sits in. Sleek gets the partner's
 * bottom sheet (Figma 2026-10-08: round X, centred "Provably fair" with the
 * shield, dark fields); the other templates keep the light centred sheet on
 * the common {@link Modal} frame, coloured from the palette (`--ui-*`).
 */
export function FairnessSheet(props: FairnessSheetProps) {
  return useIsSleek() ? <SleekSheet {...props} /> : <LightSheet {...props} />;
}

function SleekSheet({ titleId, title, onClose, children, footer }: FairnessSheetProps) {
  return (
    <SheetFrame
      onClose={onClose}
      ariaLabelledBy={titleId}
      inspectName="FairnessSheet"
      panelClassName="bg-[var(--co-FairnessSheet-background,#0E0F11)] [border-top-left-radius:var(--co-FairnessSheet-borderRadius,24px)] [border-top-right-radius:var(--co-FairnessSheet-borderRadius,24px)]"
      handleClassName="bg-[var(--co-FairnessSheet-handle,#2F3037)]"
    >
      <div className="flex flex-col gap-6" style={SLEEK_TONES}>
        <div className="relative flex h-10 items-center justify-center">
          {/* Wrapped: the skin's own `relative` would override `absolute` on the button. */}
          <div className="absolute left-0 top-0">
            <SleekButton name="FairnessCloseButton" onClick={onClose} aria-label="Close">
              <Close size={16} />
            </SleekButton>
          </div>
          <h2
            id={titleId}
            className="flex items-center gap-1.5 text-[16px] font-semibold leading-[20px] tracking-[-0.31px] text-[var(--co-FairnessSheet-text,#FFFFFF)]"
          >
            {title}
            <ShieldCheck size={18} />
          </h2>
        </div>
        <div className="flex flex-col gap-5 text-[16px] leading-[22px] text-[var(--co-FairnessSheet-text,#FFFFFF)]">{children}</div>
        <div className="flex flex-col gap-2">{footer}</div>
      </div>
    </SheetFrame>
  );
}

function LightSheet({ inspectName, titleId, title, onClose, children, footer }: FairnessSheetProps) {
  return (
    <Modal
      onClose={onClose}
      inspectName={inspectName}
      ariaLabelledBy={titleId}
      widthClassName="max-w-[463px]"
      closeSlot={
        // Fixed top-right on phones (outside the card, clear of the host's
        // safe area), overlapping the card corner from 640px — the same
        // placement as the pack disclaimer.
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="
            fixed right-4 top-[calc(1rem+var(--page-safe-top))] z-[60]
            inline-flex h-10 w-10 items-center justify-center rounded-full
            bg-black/60 text-white transition-opacity hover:opacity-90 active:scale-95
            sm:absolute sm:right-0 sm:top-0 sm:z-10
            sm:-translate-y-1/2 sm:translate-x-full
          "
        >
          <Close size={18} strokeWidth={2} />
        </button>
      }
    >
      <section
        data-co-component={inspectName}
        className="flex w-full flex-col gap-5 rounded-[28px] p-5"
        style={{
          ...LIGHT_TONES,
          backgroundColor: 'var(--ui-bg, #FFFFFF)',
          color: 'var(--ui-text, #15181E)',
        }}
      >
        <h2 id={titleId} className="flex items-center gap-2 text-[20px] font-semibold leading-[1.3] tracking-[-0.4px]">
          <ShieldCheck size={22} style={{ color: 'var(--ui-accent, #25EA1E)' }} />
          {title}
        </h2>
        <div className="flex flex-col gap-4 text-[15px] leading-[1.45]">{children}</div>
        <div className="flex flex-col gap-2">{footer}</div>
      </section>
    </Modal>
  );
}
